import { memo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { PresetButtonColors } from '@tinderapp/ui-react';
import noop from 'lodash/noop';

import config from 'Config';
import { useLever } from 'Lib/ConfigService';
import { LEVER_WEB_DISCOUNT_LOGIN_BANNER_ENABLED } from 'Lib/ConfigService/levers';
import { I13N_CATEGORY_LOGIN } from 'Lib/constants/i13nSpec';

import { getOsFamily } from 'Store/context/selectors';
import { getWebDiscountLoginBannerShown } from 'Store/purchase/selectors';

import Background from 'Containers/LoginPage/common/Background';
import LoginTitle from 'Containers/LoginPage/Mobile/LoginTitle';
import useMobileMenu from 'Containers/PageLayout/Header/hooks/useMobileMenu';
import MobileHeader from 'Containers/PageLayout/Header/Mobile';
import MobileHeaderMenu from 'Containers/PageLayout/Header/Mobile/Menu';
import useAgeGate from 'Components/AgeGate/hooks/useAgeGate';
import Loadable from 'Components/AsyncComponent';
import { I13nDiv } from 'Components/I13n';
import NativeAppsLinks from 'Components/NativeAppsLinks';
import Button from 'Components/Obsidian/Button';

import ExtraInfo from '../common/ExtraInfo';
import useDeeplinkMessage from '../hooks/useDeeplinkMessage';
import useIncentiveModal from '../hooks/useIncentiveModal';
import useInstrumentation from '../hooks/useInstrumentation';
import useLoginModal from '../hooks/useLoginModal';

const shouldFireGAEvent = Boolean(config.googleAdsRemarketingID);

const Testimonial = Loadable({
  loader: () =>
    import('Containers/LoginPage/common/Testimonial' /* webpackChunkName: "Testimonial" */),
  name: 'Testimonial',
});

const Footer = Loadable({
  loader: () => import('Containers/PageLayout/Footer' /* webpackChunkName: "Footer" */),
  name: 'Footer',
});

const MarketingBanner = Loadable({
  loader: () =>
    import(
      'Containers/Banner/components/MarketingBanner' /* webpackChunkName: "MarketingBanner" */
    ),
  name: 'MarketingBanner',
});

const ContextualStacksUtil = Loadable({
  loader: () =>
    import(
      'Containers/StacksUtil/ContextualStacksUtil/PreAuthStacksUtil' /* webpackChunkName: "ContextualStacksUtil" */
    ),
  name: 'ContextualStacksUtil',
});

const LoginMobile = () => {
  const { formatMessage } = useIntl();
  const os = useSelector(getOsFamily);
  const isWebDiscountBannerEnabled = useLever(LEVER_WEB_DISCOUNT_LOGIN_BANNER_ENABLED);
  const isWebDiscountLoginBannerShown = useSelector(getWebDiscountLoginBannerShown);
  const showDisclaimer = config.loginPage.disclaimer;

  const isIOS = os === 'iOS';

  useAgeGate({ handleCloseModal: noop });
  useInstrumentation();

  const { message: deeplinkMessage } = useDeeplinkMessage();

  const { eligibilityCheckComplete } = useIncentiveModal();

  const { openCreateAccountModal, openLoginModal } = useLoginModal();

  const handleClickLoginButton = useCallback(
    (e) => {
      e.preventDefault();
      openLoginModal();
    },
    [openLoginModal]
  );

  const { openMenu, closeMenu, isMenuOpen } = useMobileMenu();

  return (
    <I13nDiv
      className="Expand Iso(i)"
      i13nModel={{ category: I13N_CATEGORY_LOGIN, label: 'LoginMobile' }}
    >
      {isWebDiscountBannerEnabled && !isWebDiscountLoginBannerShown && <MarketingBanner isLogin />}
      <div className="Pos(a) W(100%) Z(1)">
        <MobileHeader onMenuClick={openMenu} />
        <MobileHeaderMenu
          className="Pos(f) T(0)"
          visible={isMenuOpen}
          onClose={closeMenu}
          onLoginClick={handleClickLoginButton}
        />
      </div>
      <div className="Expand">
        <div className="Bgc($c-ds-background-primary-inverse) Expand Pos(r)">
          <Background />
          <ContextualStacksUtil className="Pos(r) T(40%)" />
          <div className="Expand Pos(r)">
            <div className="C($c-ds-text-primary-overlay) Ta(c) Mx(0) D(f) Fxd(c) M(a) Expand">
              <div className="W(100%) D(f) Fxd(c) Pb(16px) M(a) Fxg(1) Fxs(0) Mt(48px) Jc(fe)">
                <LoginTitle title={deeplinkMessage} />
                {config.loginPageExtraInfo ? <ExtraInfo {...config.loginPageExtraInfo} /> : null}
                <Button
                  {...PresetButtonColors.Primary}
                  size="large"
                  className="W(80%) Mx(a)"
                  onClick={openCreateAccountModal}
                  i13nModel={{
                    message: 'Create Account',
                    conversion: shouldFireGAEvent && {
                      allow_custom_scripts: false,
                      send_to: 'AW-856138155/k5ZECIiu1-MYEKvDnpgD',
                    },
                  }}
                  data-testid="mobile-create-account"
                >
                  {formatMessage({ id: 'createAccount' })}
                </Button>
                <Button
                  {...PresetButtonColors.SecondaryOverlay}
                  size="large"
                  className="W(80%) My(20px) Mx(a)"
                  onClick={openLoginModal}
                  i13nModel={{ message: 'Login' }}
                  data-testid="mobile-log-in"
                >
                  {formatMessage({ id: 'LoginPage.logIn' })}
                </Button>
                {!eligibilityCheckComplete && (
                  <NativeAppsLinks
                    buttonClassName="W(40%) D(n)--sa C($c-ds-text-primary-overlay)"
                    showAppleStore={isIOS}
                    showPlayStore={!isIOS}
                  />
                )}
                {showDisclaimer && (
                  <div
                    className="Mt(20px) C($c-ds-text-inactive) Typs(caption-1-regular) Px(12px)"
                    data-nosnippet
                  >
                    {formatMessage({ id: 'LoginPage.illustrativePurposesDisclaimer' })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <Testimonial className="W(90%) W(100%)--xs" autoplay slidesPerView={1} />
        <Footer />
      </div>
    </I13nDiv>
  );
};

export default memo(LoginMobile);
